import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Avatar } from "../../components/ui/Avatar";
import { Explicit } from "../../components/ui/Explicit";
import { api } from "../../lib/api";

/**
 * What the search box offers while somebody is typing.
 *
 * ## Why a dropdown rather than only the results page
 *
 * Most searches here are a name somebody already knows - an artist they follow,
 * a track they just heard in a room - and for those the results page is a whole
 * navigation to reach one obvious answer. The dropdown ends the search at the
 * first keystroke that makes it unambiguous.
 *
 * The results page stays for the other kind, where somebody is looking rather
 * than fetching, and "See all results" is the door to it.
 *
 * ## One request, three kinds of thing
 *
 * `type=all` already returns tracks, artists and albums together, capped per
 * kind - so this needs no endpoint of its own. It also means "is there more
 * than we are showing" is answerable exactly, by comparing what came back with
 * what fits, rather than guessing from whether a limit was hit.
 */

interface TrackHit {
  id: number;
  title: string;
  artwork_small: string | null;
  artwork: string | null;
  explicit: boolean;
  owner?: { username: string; display_name: string };
}

interface UserHit {
  id: number;
  username: string;
  display_name: string;
  avatar: string | null;
}

interface AlbumHit {
  id: number;
  title: string;
  artwork_small?: string | null;
  artwork?: string | null;
  owner?: { username: string; display_name: string };
}

interface Results {
  tracks: TrackHit[];
  users: UserHit[];
  collections: AlbumHit[];
  short_query: boolean;
}

/** A flattened hit, so one keyboard cursor can run through every kind. */
interface Row {
  key: string;
  to: string;
  kind: "track" | "artist" | "album";
  title: string;
  subtitle: string;
  image: string | null;
  round: boolean;
  explicit?: boolean;
}

/**
 * How many rows fit before this stops being a shortcut.
 *
 * Long enough that the thing being looked for is usually in it, short enough
 * that it is still a glance. Anything past this is what the results page is
 * for, which is what the link at the bottom says.
 */
const SHOWN = 8;

export function SearchSuggest({
  term,
  open,
  onClose,
  inputRef,
}: {
  term: string;
  open: boolean;
  onClose: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  const navigate = useNavigate();
  /**
   * Which row the keyboard is on, or -1 for none.
   *
   * ## Why it does not start at zero
   *
   * It did, and that quietly changed what Enter means. Typing a term and
   * pressing Enter has always gone to the results page; with a row implicitly
   * highlighted it went to whatever happened to be first instead - so searching
   * for a word and hitting Enter opened one arbitrary artist's profile.
   *
   * A cursor nobody has moved is not a selection. Arrow keys create one.
   */
  const [active, setActive] = useState(-1);

  /*
   * Debounced, because this fires on every keystroke.
   *
   * The endpoint is rate limited at three hundred an hour and a fast typist
   * would spend that on one sentence. A quarter of a second is below what reads
   * as lag and above the gap between keystrokes.
   */
  const [debounced, setDebounced] = useState(term);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term), 250);

    return () => window.clearTimeout(timer);
  }, [term]);

  const query = debounced.trim();

  const results = useQuery({
    queryKey: ["search-suggest", query],
    queryFn: () => api.get<Results>(`/search?q=${encodeURIComponent(query)}&type=all`),
    // Two characters is where the server stops calling it a short query, and
    // below that every search matches half the catalogue anyway.
    enabled: open && query.length >= 2,
    staleTime: 30_000,
  });

  /*
   * Artists first, then tracks, then albums.
   *
   * Somebody typing a name usually wants the person; somebody typing a title
   * usually wants the track. Both orders are wrong half the time, and putting
   * artists first loses less - there are far fewer of them, so an artist match
   * on a name is a much stronger signal than a track match on the same string.
   */
  const rows = useMemo<Row[]>(() => {
    const data = results.data;

    if (!data) return [];

    return [
      ...data.users.map((user) => ({
        key: `u${user.id}`,
        to: `/${user.username}`,
        kind: "artist" as const,
        title: user.display_name,
        subtitle: `Artist · @${user.username}`,
        image: user.avatar,
        round: true,
      })),
      ...data.tracks.map((track) => ({
        key: `t${track.id}`,
        to: `/tracks/${track.id}`,
        kind: "track" as const,
        title: track.title,
        subtitle: `Song · ${track.owner?.display_name ?? "Unknown artist"}`,
        image: track.artwork_small ?? track.artwork,
        round: false,
        explicit: track.explicit,
      })),
      ...data.collections.map((album) => ({
        key: `c${album.id}`,
        to: `/albums/${album.id}`,
        kind: "album" as const,
        title: album.title,
        subtitle: `Album · ${album.owner?.display_name ?? "Unknown artist"}`,
        image: album.artwork_small ?? album.artwork ?? null,
        round: false,
      })),
    ];
  }, [results.data]);

  const visible = rows.slice(0, SHOWN);
  /*
   * Known exactly rather than inferred from a limit being hit.
   *
   * The request returns more than fits on purpose, so "there is more" is a
   * comparison instead of a guess - and a search with exactly eight results
   * does not offer a page that adds nothing.
   */
  const more = rows.length > visible.length;

  /* The cursor resets whenever the list changes under it. */
  useEffect(() => setActive(-1), [query]);

  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (document.activeElement !== inputRef.current) return;

      const total = visible.length + (more ? 1 : 0);

      if (total === 0) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActive((i) => (i + 1) % total);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        // From nothing, Up goes to the last row rather than to -2.
        setActive((i) => (i <= 0 ? total - 1 : i - 1));
      } else if (event.key === "Enter") {
        /*
         * Enter takes the highlighted row, and the form's own submit takes over
         * when nothing is highlighted - so typing a term and pressing Enter
         * still goes to the results page, which is what it always did.
         */
        const row = active >= 0 ? visible[active] : undefined;

        if (row) {
          event.preventDefault();
          navigate(row.to);
          onClose();
        }
        if (more && active === visible.length) {
          event.preventDefault();
          navigate(`/search?q=${encodeURIComponent(query)}`);
          onClose();
        }
      } else if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [open, visible, more, active, navigate, onClose, inputRef, query]);

  if (!open || query.length < 2) return null;

  const empty = results.isSuccess && rows.length === 0;

  return (
    <div
      role="listbox"
      aria-label="Search results"
      data-search-suggest
      /*
       * As wide as the field, and never narrower than this.
       *
       * The field shrinks with the viewport - it is `flex-1` between the logo
       * and the nav - and the list following it down to a few hundred pixels
       * would truncate every title. The floor is what the rows actually need;
       * the cap keeps it on screen when the window is smaller than the floor.
       */
      className="absolute left-0 right-0 top-[calc(100%+8px)] z-40 w-[max(100%,min(30rem,calc(100vw-3rem)))] overflow-hidden rounded-[var(--radius-panel)] border border-line-lit bg-raised py-1.5 shadow-2xl"
    >
      {results.isPending && (
        <p className="px-4 py-3 font-mono text-[11px] text-ghost">searching…</p>
      )}

      {empty && (
        <p className="px-4 py-3 text-sm text-faint">
          Nothing matching “{query}”
        </p>
      )}

      {visible.map((row, i) => (
        <button
          key={row.key}
          type="button"
          role="option"
          aria-selected={i === active}
          data-search-hit={row.kind}
          onPointerEnter={() => setActive(i)}
          onPointerDown={(event) => {
            // Or the input blurs first, the dropdown closes, and the click
            // lands on whatever the page put in its place.
            event.preventDefault();
            navigate(row.to);
            onClose();
          }}
          className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
            i === active ? "bg-slate" : ""
          }`}
        >
          {row.kind === "artist" ? (
            <Avatar src={row.image} name={row.title} size={40} />
          ) : row.image ? (
            <img
              src={row.image}
              alt=""
              className="h-10 w-10 shrink-0 rounded-[var(--radius-control)] object-cover"
            />
          ) : (
            <span
              aria-hidden
              className="h-10 w-10 shrink-0 rounded-[var(--radius-control)]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg, var(--color-blank) 0 6px, var(--color-blank-alt) 6px 12px)",
              }}
            />
          )}

          <span className="min-w-0 flex-1">
            <span className="flex min-w-0 items-center gap-1.5">
              {row.explicit && <Explicit />}
              <span className="truncate text-sm font-medium text-ink">
                {row.title}
              </span>
            </span>
            <span className="meta block truncate text-faint">{row.subtitle}</span>
          </span>
        </button>
      ))}

      {more && (
        <button
          type="button"
          role="option"
          aria-selected={active === visible.length}
          data-search-see-all
          onPointerEnter={() => setActive(visible.length)}
          onPointerDown={(event) => {
            event.preventDefault();
            navigate(`/search?q=${encodeURIComponent(query)}`);
            onClose();
          }}
          className={`mt-1 flex w-full items-center justify-between border-t border-line px-4 py-2.5 text-left text-sm transition-colors ${
            active === visible.length ? "bg-slate text-ink" : "text-soft"
          }`}
        >
          See all results
          <span aria-hidden className="font-mono text-[11px] text-faint">
            →
          </span>
        </button>
      )}
    </div>
  );
}
